import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Button, Dropdown, Icon, Label } from "semantic-ui-react"
// @ts-ignore
import { languages, setInterpolationDictionary } from "simplydo/core/i18n"
import type { OpenAPI } from "simplydo/interfaces"
import styled, { ThemeContext } from "styled-components"

import actions from "actions"
import api, { websocketApi } from "api"
import ManageLinksModal from "components/admin/Links/ManageLinksModal"
import { useOrgNavBar } from "components/admin/Settings/Branding"
import MessageTray from "components/lib/Messaging/MessageTray"
import HighlightLink from "components/lib/Navigation/HighlightLink"
import HoverLinkDropdown, { HoverLink } from "components/lib/Navigation/HoverLinkDropdown"
import NotificationTray from "components/lib/Notifications"
import OmniBar from "components/lib/OmniBar"
import { FakeLink } from "components/lib/UI"
import { useAppDispatch, useAppSelector } from "store"
import util from "utils/utils"

import OrgSwitcherTray from "../OrgSwitcherTray"

const StyledNavBar = styled.header`
  background: rgb(255, 255, 255);
  flex: 0 0 50px;
  gap: ${({ theme }) => (theme.sizes.isMobile ? 10 : 20)}px;
  position: sticky;
  width: 100%;
  top: 0;
  border-bottom: 1px solid rgb(204, 204, 204);
  display: flex;
  align-content: stretch;
  align-items: stretch;
  justify-content: space-between;
  z-index: 1000;
`

export const StyledNavBarSubSegment = styled.div`
  background-color: #f4f4f4;
  border-left: 1px solid #dedede;
  padding: 0 16px 0 14px;
  margin-left: ${({ theme }) => (theme.sizes.isMobile ? "10px" : "20px")};
  height: 50px;
  margin-right: -10px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledNavBarSegment = styled.div<{ $alignRight?: boolean }>`
  display: flex;
  align-items: center;
  ${({ $alignRight }) => $alignRight && "justify-content: flex-end;"}
  &:first-of-type {
    margin-left: 10px;
  }
  &:last-of-type {
    margin-right: 10px;
  }
`

const StyledLogo = styled(Link)`
  margin-right: 10px;
  img {
    vertical-align: middle;
    max-height: 40px;
    max-width: ${({ theme }) => (!theme.sizes.isComputer ? 100 : 150)}px;
  }
`

const ActionContainer = styled.div`
  display: flex;
  flex: 1;
  flex-grow: 1;
  align-items: center;
  text-transform: capitalize;
`

const StyledNavIcon = styled.div`
  display: inline-block;
  font-size: 20px;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  background-color: rgb(240, 240, 240);
  transition: background-color 0.25s;
  cursor: pointer;
  margin-right: ${({ theme }) => (theme.sizes.isMobile ? "10px" : "20px")};
  position: relative;
  .icon {
    display: block;
    margin: 5px auto;
  }
  a,
  .icon {
    transition: color 0.25s;
    color: rgb(120, 136, 150);
  }
  &:hover {
    background-color: ${({ theme }) => theme.accent};
    a,
    .icon {
      color: ${({ theme }) => (theme.shouldBeWhiteOnAccent ? "white" : "#4a4544")};
    }
  }
`

const StyledNavAvatar = styled.div<{ $user?: OpenAPI.Schemas["User"] }>`
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-radius: 50%;
  overflow: hidden;
  background-color: white;
  background-size: cover;
  background-position: center center;
  margin-right: 0px;
  background-image: ${({ $user }) => `url(${util.mixinCssUrlFallback(util.avatarUrl($user), util.avatarUrl())})`};
  outline: 1px solid #e2e2e2;
  transition: box-shadow 0.25s;
  &:hover {
    box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
  }
`

const StyledMobileSearchBar = styled.div`
  padding-top: 0px;
  padding-bottom: 0px;
  background-color: #fff;
  position: fixed;
  width: 100%;
  z-index: 100;
  top: 50px;
`

export const SimpleNavBar = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const theme = useContext(ThemeContext)
  const navigate = useNavigate()

  const [userBarOpen, setUserBarOpen] = useState(false)

  const urlOrganisation = useAppSelector((state) => state.organisations.urlOrganisation)

  const organisation = user?.ownerOrganisation || urlOrganisation

  const switchOrganisation = useCallback(
    (orgId) => {
      api.users.switchOrganisation(
        user._id,
        orgId,
        (data) => {
          dispatch(actions.user.switchOrganisation(data.organisation))
        },
        () => {},
      )
    },
    [user?._id, dispatch],
  )

  const unGhost = () => {
    api.users.unGhost(
      () => {
        dispatch(actions.user.unGhost())
      },
      () => {},
    )
  }

  const logout = () => {
    api.auth.logout(
      () => {
        navigate("/")
      },
      () => {},
    )
  }

  return (
    <StyledNavBar>
      <StyledNavBarSegment style={{ flex: 1, flexGrow: 1 }}>
        <StyledLogo to="/">
          <img alt="" src={organisation?.darkLogoUrl} />
        </StyledLogo>
      </StyledNavBarSegment>

      <StyledNavBarSegment $alignRight style={{ flex: 1, flexGrow: 1 }}>
        {user && theme.sizes.isComputer && (
          <StyledNavIcon onClick={() => window.$chatwoot.toggle()}>
            <Icon name="question circle" />
          </StyledNavIcon>
        )}
        {user ? (
          <Dropdown
            trigger={<StyledNavAvatar onClick={() => setUserBarOpen((prev) => !prev)} $user={user} />}
            direction="left"
            pointing="top right"
            icon={null}
            open={userBarOpen}
            onClose={() => setUserBarOpen(false)}
          >
            <Dropdown.Menu style={{ margin: "10px -4px 0 0" }}>
              <Dropdown.Header>
                <Icon name="user circle" /> {user?.profile?.fullName}
              </Dropdown.Header>
              {user?.ownerOrganisations?.length > 1 && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Header>Switch account</Dropdown.Header>
                  {user.ownerOrganisations.map((o) => (
                    <Dropdown.Item key={o._id} onClick={() => switchOrganisation(o._id)}>
                      <span style={{ width: 50, marginRight: 5 }}>
                        <img
                          alt={`${o.name} logo`}
                          src={o.darkLogoUrl}
                          style={{ maxWidth: 50, maxHeight: 35, verticalAlign: "middle" }}
                        />
                      </span>
                      {o.name}
                    </Dropdown.Item>
                  ))}
                </>
              )}
              <Dropdown.Divider />
              {user?.ghostingAs ? (
                <Dropdown.Item onClick={unGhost}>
                  <Icon name="power off" /> Unghost user
                </Dropdown.Item>
              ) : (
                <Dropdown.Item onClick={logout}>
                  <Icon name="power off" /> {t("generic.logout")}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        ) : null}

        {user ? <OrgSwitcherTray /> : null}
      </StyledNavBarSegment>
    </StyledNavBar>
  )
}

const NavBar = () => {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const theme = useContext(ThemeContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [omniSearchOpen, setOmniSearchOpen] = useState(false)
  const [userBarOpen, setUserBarOpen] = useState(false)
  const [manageLinksModalOpen, setManageLinksModalOpen] = useState(false)
  const profileUpdateRef = useRef(null)
  const selectedLanguage = languages.filter((l) => l.key === (i18n.language || "en"))[0]

  const urlOrganisation = useAppSelector((state) => state.organisations.urlOrganisation)
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated)
  const newMessageCount = useAppSelector((state) => state.messages.newMessageCount)

  const organisation = user?.ownerOrganisation || urlOrganisation
  const orgId = organisation?._id

  const orgNavBar = useOrgNavBar()

  const visibleNavItems = orgNavBar.entries.slice(0, 5)
  const moreNavItems = orgNavBar.entries.slice(5)

  const [pinnedPages, setPinnedPages] = useState<OpenAPI.GET<"/pages/pinned/{for_type}/{for_id}">["response"]["pages"]>(
    [],
  )

  const updateProfile = useCallback(
    (profile) => {
      dispatch(actions.user.updateProfile(profile))
    },
    [dispatch],
  )

  const getPinnedPages = useCallback(() => {
    api.pages.getPinned(
      "organisation",
      orgId,
      ({ pages: newPages }) => {
        setPinnedPages(newPages)
      },
      () => {},
    )
  }, [orgId])

  useEffect(() => {
    getPinnedPages()
  }, [getPinnedPages])

  const unGhost = () => {
    api.users.unGhost(
      () => {
        dispatch(actions.user.unGhost())
      },
      () => {},
    )
  }

  const logout = () => {
    api.auth.logout(
      () => {
        navigate("/")
      },
      () => {},
    )
  }

  const switchOrganisation = useCallback(
    (orgId) => {
      api.users.switchOrganisation(
        user._id,
        orgId,
        (data) => {
          dispatch(actions.user.switchOrganisation(data.organisation))
        },
        () => {},
      )
    },
    [user?._id, dispatch],
  )

  // Subscribe to websocket updates to check if Microsoft SSO has applied a profile pic automatically
  // It's highly possible the websocket subscription "misses" a Microsoft profile picture update; Simply because the event that sets the profile picture could conceivably finish in the nether zone between the point where /users/me() has been returned to the client, and before we are subscribed to websockets.
  // There's nothing we can do about this - Therefore, to cover this gap, if we know the user has logged in with SSO, we make a single request to get their profile and load in the profile image if it's not already there.
  const userId = user?._id
  const isSsoUser = !!user?.ssoUser
  const userHasAvatarImage = !!user?.profile?.avatar
  useEffect(() => {
    if (userId && isSsoUser && !userHasAvatarImage) {
      profileUpdateRef.current = websocketApi.subscribe(
        `userProfileUpdated-${userId}`,
        (data) => {
          // @ts-ignore
          updateProfile(data.profile)
        },
        { receiveErrorUpdates: false, receiveUserUpdates: false },
      )
      api.users.get(
        userId,
        (data) => {
          if (data?.profile?.avatar && data?.profile?.avatarUrl) {
            updateProfile({
              avatar: data.profile.avatar,
              avatarUrl: util.avatarUrl(data),
              avatarWasSetByExternalSource: !!data.profile.avatarWasSetByExternalSource,
            })
          }
        },
        () => {},
      )
    }
    return () => {
      if (profileUpdateRef.current) {
        profileUpdateRef.current.unsubscribe()
      }
    }
  }, [isSsoUser, updateProfile, userId, userHasAvatarImage])

  const shouldShowMoreMenu = useMemo(
    () => moreNavItems.length > 0 || !!pinnedPages.length,
    [moreNavItems.length, pinnedPages.length],
  )

  return (
    <>
      <ManageLinksModal isOpen={manageLinksModalOpen} onClose={() => setManageLinksModalOpen(false)} />

      <StyledNavBar>
        <StyledNavBarSegment style={{ flex: 1, flexGrow: 1 }}>
          <StyledLogo to="/">
            <img alt="" src={organisation?.darkLogoUrl} />
          </StyledLogo>
          <ActionContainer>
            {!theme.sizes.isComputer ? (
              user && (
                <Dropdown
                  icon={null}
                  trigger={
                    <StyledNavIcon theme={theme}>
                      <Icon name="bars" />
                    </StyledNavIcon>
                  }
                  style={{ zIndex: 999 }}
                >
                  <Dropdown.Menu>
                    {user &&
                      orgNavBar.entries.map((item) => {
                        if (
                          item.id === "groups" &&
                          !user.groupsAvailable &&
                          !util.hasPermission(user, "org.createGroups", user.ownerOrganisation._id)
                        ) {
                          return null
                        }

                        if (item.type === "link") {
                          return (
                            <Dropdown.Item
                              key={item.id}
                              as={Link}
                              to={item.location}
                              target={item.external ? "_blank" : undefined}
                              content={item.title}
                            />
                          )
                        }
                        return (
                          <Dropdown.Item
                            key={item.id}
                            as={Link}
                            to={item.location}
                            selected={
                              item.id === "home"
                                ? location.pathname === "/"
                                : location.pathname.startsWith(`/${item.location}`)
                            }
                            content={
                              <span style={{ textTransform: "capitalize" }}>{item.title || t(item.translate)}</span>
                            }
                          />
                        )
                      })}
                  </Dropdown.Menu>
                </Dropdown>
              )
            ) : (
              <React.Fragment>
                {user &&
                  visibleNavItems.map((item) => {
                    if (
                      item.id === "groups" &&
                      (user.groupsAvailable || util.hasPermission(user, "org.createGroups", user.ownerOrganisation._id))
                    ) {
                      return (
                        <HoverLinkDropdown
                          key="groups"
                          type="groups"
                          title={t("navbar.yourGroups")}
                          trigger={
                            <HighlightLink
                              as={Link}
                              to={`/${item.location}`}
                              selected={util.activePath(/^\/groups([A-z0-9/])*\w*/g)}
                              content={t(item.translate)}
                            />
                          }
                        />
                      )
                    }
                    if (item.id === "admin") {
                      if (util.hasPermission(user, "org.viewDashboard", user?.ownerOrganisation._id)) {
                        return (
                          <HoverLinkDropdown
                            key="admin"
                            trigger={
                              <HighlightLink
                                as={Link}
                                to={`/${item.location}`}
                                selected={util.activePath(/^\/admin([A-z0-9/])*\w*/g)}
                                content={t(item.translate)}
                              />
                            }
                            title={t("navbar.quickAccess")}
                            content={
                              <HoverLink onClick={() => setManageLinksModalOpen(true)}>
                                <span>
                                  <Icon name="cog" /> {t("navbar.manageShortLinks")}
                                </span>
                              </HoverLink>
                            }
                          />
                        )
                      }
                      return null
                    }

                    if (item.type === "link") {
                      const itemLocationUrl = new URL(item.location, window.location.origin)
                      const itemLocationPath = itemLocationUrl.pathname

                      const isSelected = itemLocationPath !== "/" && location.pathname === itemLocationPath

                      return (
                        <HighlightLink
                          key={item.id}
                          as={Link}
                          to={item.location}
                          target={item.external ? "_blank" : undefined}
                          content={item.title}
                          selected={isSelected}
                          style={{ whiteSpace: "nowrap" }}
                        />
                      )
                    }
                    return (
                      <HighlightLink
                        key={item.id}
                        as={Link}
                        to={item.location}
                        selected={
                          item.id === "home"
                            ? location.pathname === "/"
                            : location.pathname.startsWith(`/${item.location}`)
                        }
                        content={item.title || t(item.translate)}
                        style={{ whiteSpace: "nowrap" }}
                      />
                    )
                  })}
                {user && shouldShowMoreMenu ? (
                  <HoverLinkDropdown
                    trigger={
                      <FakeLink style={{ display: "block", margin: "5px 17px" }} withBorder>
                        {t("generic.more")}
                      </FakeLink>
                    }
                    content={
                      <>
                        {moreNavItems.length > 0 && <span>{t("navbar.moreLocations")}</span>}
                        {moreNavItems.map((item) => {
                          if (item.type === "link") {
                            return (
                              <HoverLink as={Link} to={item.location} target={item.external ? "_blank" : undefined}>
                                {item.title}
                              </HoverLink>
                            )
                          }

                          return (
                            <HoverLink key={item.id} as={Link} to={item.location}>
                              <span style={{ textTransform: "capitalize" }}>{item.title || t(item.translate)}</span>
                            </HoverLink>
                          )
                        })}
                        {pinnedPages && pinnedPages.length > 0 ? (
                          <>
                            <span>{t("navbar.pinnedPages")}</span>
                            {pinnedPages.map((page) => (
                              <HoverLink key={page._id} as="a" target="_blank" rel="noreferrer" href={page.link}>
                                {page.title}
                              </HoverLink>
                            ))}
                          </>
                        ) : null}
                      </>
                    }
                  />
                ) : null}
              </React.Fragment>
            )}
          </ActionContainer>
        </StyledNavBarSegment>

        {user && theme.sizes.isComputer && (
          <StyledNavBarSegment style={{ flex: 1, flexGrow: 2, maxWidth: 600 }}>
            <OmniBar />
          </StyledNavBarSegment>
        )}

        <StyledNavBarSegment $alignRight style={{ flex: 1, flexGrow: 1 }}>
          {user && !theme.sizes.isComputer && (
            <StyledNavIcon onClick={() => setOmniSearchOpen((prevOpen) => !prevOpen)}>
              <Icon name="search" />
            </StyledNavIcon>
          )}
          {user ? (
            <NotificationTray
              // @ts-ignore
              style={{ marginRight: theme.sizes.isMobile ? 10 : 20 }}
            />
          ) : null}
          {user &&
            !user.ghostingAs &&
            user?.ownerOrganisation?.enabledFeatures.includes("directMessaging") &&
            (theme.sizes.isMobile ? (
              <StyledNavIcon>
                <Link to={"/messagetray"}>
                  <Icon name="envelope" />
                  {newMessageCount > 0 && (
                    <Label circular size="small" color="red" floating style={{ top: "0.5em", left: "auto" }}>
                      {newMessageCount}
                    </Label>
                  )}
                </Link>
              </StyledNavIcon>
            ) : (
              <MessageTray style={{ marginRight: 20 }} />
            ))}
          {/* @ts-ignore */}
          {user && theme.sizes.isComputer && (
            <StyledNavIcon onClick={() => window.$chatwoot.toggle()}>
              <Icon name="question circle" />
            </StyledNavIcon>
          )}
          {user ? (
            <Dropdown
              trigger={<StyledNavAvatar onClick={() => setUserBarOpen((prev) => !prev)} $user={user} />}
              direction="left"
              pointing="top right"
              icon={null}
              open={userBarOpen}
              onClose={() => setUserBarOpen(false)}
            >
              <Dropdown.Menu style={{ margin: "10px -4px 0 0" }}>
                <Dropdown.Header>
                  <Icon name="user circle" /> {user?.profile?.fullName}
                </Dropdown.Header>
                <Link to={`/users/${user._id}`} className="item">
                  <i className="user icon"></i> {t("generic.profile")}
                </Link>
                <Link to="/invitations" className="item">
                  <i className="paper plane icon"></i> {t("generic.invitations")}
                </Link>
                <Link to="/preferences" className="item">
                  <i className="cogs icon"></i> {t("generic.account")}
                </Link>
                <Link to="/achievements" className="item">
                  <i className="trophy icon"></i> {t("generic.achievements")}
                </Link>
                {user?.ownerOrganisations?.length > 1 && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Header>Switch account</Dropdown.Header>
                    {user.ownerOrganisations.map((o) => (
                      <Dropdown.Item key={o._id} onClick={() => switchOrganisation(o._id)}>
                        <span style={{ width: 50, marginRight: 5 }}>
                          <img
                            alt={`${o.name} logo`}
                            src={o.darkLogoUrl}
                            style={{ maxWidth: 50, maxHeight: 35, verticalAlign: "middle" }}
                          />
                        </span>
                        {o.name}
                      </Dropdown.Item>
                    ))}
                  </>
                )}
                {(util.hasPermission(user, "super.viewDashboard") ||
                  util.hasPermission(user, "org.managePermissions", user.ownerOrganisation)) && <Dropdown.Divider />}
                {util.hasPermission(user, "super.viewDashboard") && (
                  <Link to="/superadmin" className="item">
                    <i className="rocket icon"></i> Superadmin
                  </Link>
                )}
                <Dropdown.Divider />
                {user?.ghostingAs ? (
                  <Dropdown.Item onClick={unGhost}>
                    <Icon name="power off" /> Unghost user
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item onClick={logout}>
                    <Icon name="power off" /> {t("generic.logout")}
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          ) : null}

          {user ? <OrgSwitcherTray /> : null}
          {!user && isAuthenticated && (
            <Button color="teal" content="Logout" onClick={() => api?.options?.onLogout?.()} />
          )}
          {!user && !isAuthenticated && !location.pathname.startsWith("/challenges/") ? ( // Don't show language chooser on unauthenticated challenge page
            <Dropdown
              direction="left"
              pointing="top right"
              trigger={<span>{selectedLanguage?.name ?? "Language"}</span>}
            >
              <Dropdown.Menu>
                {languages.map((l) => (
                  <Dropdown.Item
                    key={l.key}
                    onClick={() => {
                      i18n.changeLanguage(l.key)
                      setInterpolationDictionary(organisation?.terminology)
                    }}
                  >
                    {l.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : null}

          {!user && location.pathname.startsWith("/challenges/") ? ( // Don't show language chooser on unauthenticated challenge page
            <Button
              content="Log in or register"
              primary
              onClick={() => {
                navigate(`/login?then=${location.pathname}`)
              }}
            />
          ) : null}
        </StyledNavBarSegment>
      </StyledNavBar>

      {!theme.sizes.isComputer && omniSearchOpen && (
        <StyledMobileSearchBar>
          <OmniBar onClose={() => setOmniSearchOpen(false)} />
        </StyledMobileSearchBar>
      )}
    </>
  )
}

export default NavBar
